"use client";

import * as React from "react";
import { cn } from "@repo/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";

type LinkComponentType = React.ComponentType<
  { href: string; className?: string; children: React.ReactNode } & Record<
    string,
    unknown
  >
>;

function DefaultLink({
  href,
  className,
  children,
  ...props
}: { href: string; className?: string; children: React.ReactNode } & Record<
  string,
  unknown
>) {
  return (
    <a href={href} className={className} {...props}>
      {children}
    </a>
  );
}

const SidebarContext = React.createContext<{
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  linkComponent: LinkComponentType;
}>({
  collapsed: false,
  setCollapsed: () => {},
  linkComponent: DefaultLink,
});

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a Sidebar");
  }
  return context;
}

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultCollapsed?: boolean;
  collapsible?: boolean;
  linkComponent?: LinkComponentType;
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      className,
      children,
      defaultCollapsed = false,
      collapsible = true,
      linkComponent = DefaultLink,
      ...props
    },
    ref
  ) => {
    const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

    return (
      <SidebarContext.Provider
        value={{ collapsed, setCollapsed, linkComponent }}
      >
        <div
          ref={ref}
          className={cn(
            "relative flex h-full flex-col bg-surface border-r border-border/40 transition-all duration-200",
            collapsed ? "w-16" : "w-60",
            className
          )}
          {...props}
        >
          {children}
          {collapsible && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border border-border/60 bg-surface shadow-sm hover:shadow-md hover:bg-muted/50"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </Button>
          )}
        </div>
      </SidebarContext.Provider>
    );
  }
);
Sidebar.displayName = "Sidebar";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SidebarHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, children, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center px-4 py-4",
          collapsed ? "justify-center" : "justify-between",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarHeader.displayName = "SidebarHeader";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden py-4",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarContent.displayName = "SidebarContent";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("border-t border-border/40 p-4", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarFooter.displayName = "SidebarFooter";

interface SidebarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string;
}

const SidebarGroup = React.forwardRef<HTMLDivElement, SidebarGroupProps>(
  ({ className, label, children, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div ref={ref} className={cn("mb-1", className)} {...props}>
        {label && !collapsed && (
          <div className="mb-1.5 px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/50">
            {label}
          </div>
        )}
        <div className="space-y-0.5 px-2">{children}</div>
      </div>
    );
  }
);
SidebarGroup.displayName = "SidebarGroup";

interface SidebarItemProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "href"
> {
  icon?: React.ReactNode;
  active?: boolean;
  badge?: React.ReactNode;
  href?: string;
}

const SidebarItem = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  SidebarItemProps
>(({ className, icon, active, badge, children, href, ...props }, ref) => {
  const { collapsed, linkComponent: LinkComponent } = useSidebar();

  const itemClassName = cn(
    "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors duration-150",
    "hover:bg-muted/60 hover:text-foreground",
    active
      ? "bg-primary/[0.08] text-primary font-semibold"
      : "text-muted-foreground",
    collapsed && "justify-center px-2",
    className
  );

  const content = (
    <>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {!collapsed && (
        <>
          <span className="flex-1 text-left">{children}</span>
          {badge && <span className="flex-shrink-0">{badge}</span>}
        </>
      )}
    </>
  );

  if (href) {
    return (
      <LinkComponent
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        className={itemClassName}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </LinkComponent>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={itemClassName}
      {...props}
    >
      {content}
    </button>
  );
});
SidebarItem.displayName = "SidebarItem";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface SidebarSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

const SidebarSeparator = React.forwardRef<
  HTMLDivElement,
  SidebarSeparatorProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("my-2 h-px bg-border/30 mx-4", className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarItem,
  SidebarSeparator,
};
